/**
 * API client with cookie-based authentication
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance with credentials
const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true, // This enables cookies to be sent with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth header as fallback during migration
apiClient.interceptors.request.use(
  (config) => {
    // Fallback to localStorage token if cookie auth fails
    const token = localStorage.getItem('admin_token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear local storage and redirect to login
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      
      // Only redirect if we're on an admin page
      if (window.location.pathname.startsWith('/admin') && 
          !window.location.pathname.includes('/login') &&
          !window.location.pathname.includes('/set-password')) {
        window.location.href = '/panel-gestion-8k2m/acceso';
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// Auth functions
export const authApi = {
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    // Store user data in localStorage (but token is in httpOnly cookie)
    if (response.data.user) {
      localStorage.setItem('admin_user', JSON.stringify(response.data.user));
      // Keep token in localStorage for backwards compatibility during migration
      if (response.data.token) {
        localStorage.setItem('admin_token', response.data.token);
      }
    }
    return response.data;
  },
  
  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Ignore errors on logout
    }
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  },
  
  getProfile: () => apiClient.get('/profile'),
  updateProfile: (data) => apiClient.put('/profile', data),
};

// Property functions
export const propertyApi = {
  getAll: (params) => apiClient.get('/properties', { params }),
  getById: (id) => apiClient.get(`/properties/${id}`),
  create: (data) => apiClient.post('/properties', data),
  createDual: (data) => apiClient.post('/properties/dual', data),
  update: (id, data) => apiClient.put(`/properties/${id}`, data),
  delete: (id) => apiClient.delete(`/properties/${id}`),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// CRM functions
export const crmApi = {
  getStats: () => apiClient.get('/crm/stats'),
  
  // Contacts
  getContacts: (params) => apiClient.get('/crm/contacts', { params }),
  getContact: (id) => apiClient.get(`/crm/contacts/${id}`),
  createContact: (data) => apiClient.post('/crm/contacts', data),
  updateContact: (id, data) => apiClient.put(`/crm/contacts/${id}`, data),
  deleteContact: (id) => apiClient.delete(`/crm/contacts/${id}`),
  addInteraction: (id, data) => apiClient.post(`/crm/contacts/${id}/interactions`, data),
  
  // Stages
  getStages: () => apiClient.get('/crm/stages'),
  createStage: (data) => apiClient.post('/crm/stages', data),
  updateStage: (id, data) => apiClient.put(`/crm/stages/${id}`, data),
  deleteStage: (id) => apiClient.delete(`/crm/stages/${id}`),
};

// Team functions
export const teamApi = {
  getAll: () => apiClient.get('/team'),
  create: (data) => apiClient.post('/team', data),
  update: (id, data) => apiClient.put(`/team/${id}`, data),
  delete: (id) => apiClient.delete(`/team/${id}`),
};
